import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  FlightTakeoff as FlightIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  ArrowRightAlt as ArrowIcon,
} from '@mui/icons-material';

const GestionVuelos = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Datos para formulario
  const [rutas, setRutas] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [clientes, setClientes] = useState([]);
  
  // Vuelos abiertos agrupados por origen
  const [vuelosPorOrigen, setVuelosPorOrigen] = useState({});
  const [expandedOrigins, setExpandedOrigins] = useState({});

  // Form data
  const [formData, setFormData] = useState({
    ruta_id: '',
    awb: '',
    master: '',
    equipo_id: '',
    cliente_id: '',
  });

  // Ruta seleccionada para preview
  const [selectedRuta, setSelectedRuta] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rutasRes, equiposRes, clientesRes, vuelosRes] = await Promise.all([
        api.get(endpoints.rutas.list),
        api.get(endpoints.equipos),
        api.get(endpoints.oficina.clientesfact),
        api.get(`${endpoints.vuelos.list}?limit=200`),
      ]);

      setRutas(rutasRes.data.data?.rutas || []);
      setEquipos(equiposRes.data.data || []);
      setClientes(clientesRes.data.data || []);

      // Agrupar vuelos abiertos por origen
      const vuelos = vuelosRes.data.data?.vuelos || [];
      const abiertos = vuelos.filter(v => !v.cerrado);
      const grouped = abiertos.reduce((acc, v) => {
        const origen = v.origen || 'Sin Origen';
        if (!acc[origen]) acc[origen] = [];
        acc[origen].push(v);
        return acc;
      }, {});
      setVuelosPorOrigen(grouped);

      // Expandir todos los orígenes por defecto
      const expanded = {};
      Object.keys(grouped).forEach(k => expanded[k] = true);
      setExpandedOrigins(expanded);

    } catch (error) {
      console.error('Error al cargar datos:', error);
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'ruta_id') {
      const ruta = rutas.find((r) => r.id == value);
      setSelectedRuta(ruta || null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.ruta_id) {
      setError('Seleccione una ruta');
      return;
    }
 
    if (!formData.equipo_id) {
      setError('Seleccione un equipo');
      return;
    }
    if (!formData.cliente_id) {
      setError('Seleccione un cliente');
      return;
    }

    try {
      setSaving(true);
      await api.post(endpoints.vuelos.create, {
        awb: formData.awb.toUpperCase(),
        master: formData.master,
        ruta_id: parseInt(formData.ruta_id),
        equipo_id: parseInt(formData.equipo_id),
        cliente_id: parseInt(formData.cliente_id),
        fecha: new Date().toISOString().split('T')[0], // HOY AUTOMÁTICO
      });

      setSuccess('Vuelo creado exitosamente con fecha de hoy');
      setFormData({ ruta_id: '', awb: '', master: '', equipo_id: '', cliente_id: '' });
      setSelectedRuta(null);
      fetchData(); // Refrescar lista
    } catch (error) {
      setError(error.response?.data?.message || 'Error al crear el vuelo');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vuelo) => {
    if (!confirm(`¿Eliminar el vuelo ${vuelo.awb}?`)) return;

    try {
      await api.delete(endpoints.vuelos.delete, { data: { id: vuelo.id } });
      setSuccess('Vuelo eliminado correctamente');
      fetchData();
    } catch (error) {
      setError(error.response?.data?.message || 'Error al eliminar');
    }
  };

  const toggleOrigin = (origen) => {
    setExpandedOrigins(prev => ({ ...prev, [origen]: !prev[origen] }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FlightIcon sx={{ fontSize: 48 }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Gestión de Vuelos
              </Typography>
              <Typography sx={{ opacity: 0.9 }}>
                Pallet Control | Creación y seguimiento
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Formulario Crear Vuelo */}
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
            Crear Nuevo Vuelo
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Ruta */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Ruta</InputLabel>
                  <Select
                    name="ruta_id"
                    value={formData.ruta_id}
                    onChange={handleInputChange}
                    label="Ruta"
                  >
                    <MenuItem value="">Seleccionar ruta</MenuItem>
                    {rutas.map((ruta) => (
                      <MenuItem key={ruta.id} value={ruta.id}>
                        {ruta.code} - {ruta.origen} → {ruta.destino}
                        {ruta.escala > 0 && ` (vía ${[ruta.esc1, ruta.esc2, ruta.esc3].filter(Boolean).join(', ')})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

          
              {/* Master */}
              <Grid item xs={12} md={4}>
                <TextField
                  label="Master"
                  name="master"
                  value={formData.master}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="Ej: MIA, AMS, etc."
                />
              </Grid>

              {/* Equipo */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Equipo</InputLabel>
                  <Select
                    name="equipo_id"
                    value={formData.equipo_id}
                    onChange={handleInputChange}
                    label="Equipo"
                  >
                    <MenuItem value="">Seleccionar equipo</MenuItem>
                    {equipos.map((eq) => (
                      <MenuItem key={eq.id} value={eq.id}>
                        {eq.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Cliente */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Cliente</InputLabel>
                  <Select
                    name="cliente_id"
                    value={formData.cliente_id}
                    onChange={handleInputChange}
                    label="Cliente"
                  >
                    <MenuItem value="">Seleccionar cliente</MenuItem>
                    {clientes.map((cli) => (
                      <MenuItem key={cli.id} value={cli.id}>
                        {cli.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Botón */}
              <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                  disabled={saving}
                  fullWidth
                  sx={{ height: 56 }}
                >
                  {saving ? 'Creando...' : 'Crear Vuelo'}
                </Button>
              </Grid>
            </Grid>

            {/* Preview de Ruta Seleccionada */}
            {selectedRuta && (
              <Paper sx={{ mt: 2, p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
                <Typography variant="subtitle2" color="success.dark" gutterBottom>
                  Ruta Seleccionada: {selectedRuta.code}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip label={selectedRuta.origen} size="small" color="success" />
                  {selectedRuta.escala >= 1 && selectedRuta.esc1 && (
                    <>
                      <ArrowIcon fontSize="small" />
                      <Chip label={selectedRuta.esc1} size="small" variant="outlined" />
                    </>
                  )}
                  {selectedRuta.escala >= 2 && selectedRuta.esc2 && (
                    <>
                      <ArrowIcon fontSize="small" />
                      <Chip label={selectedRuta.esc2} size="small" variant="outlined" />
                    </>
                  )}
                  {selectedRuta.escala >= 3 && selectedRuta.esc3 && (
                    <>
                      <ArrowIcon fontSize="small" />
                      <Chip label={selectedRuta.esc3} size="small" variant="outlined" />
                    </>
                  )}
                  <ArrowIcon fontSize="small" />
                  <Chip label={selectedRuta.destino} size="small" color="error" />
                </Box>
              </Paper>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Vuelos Abiertos por Origen */}
      <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
        Vuelos Abiertos por Origen
      </Typography>

      {Object.keys(vuelosPorOrigen).length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <FlightIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">No hay vuelos abiertos</Typography>
        </Card>
      ) : (
        Object.entries(vuelosPorOrigen).map(([origen, vuelos]) => (
          <Card key={origen} sx={{ mb: 2 }}>
            <CardContent
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                py: 1.5,
                '&:hover': { bgcolor: 'action.hover' },
              }}
              onClick={() => toggleOrigin(origen)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip label={origen} color="primary" />
                <Typography variant="body2" color="text.secondary">
                  {vuelos.length} vuelo(s)
                </Typography>
              </Box>
              <IconButton size="small">
                {expandedOrigins[origen] ? <CollapseIcon /> : <ExpandIcon />}
              </IconButton>
            </CardContent>

            <Collapse in={expandedOrigins[origen]}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell><Typography fontWeight="bold">AWB - Master</Typography></TableCell>
                      <TableCell>Origen</TableCell>
                      <TableCell>Destino</TableCell>
                      <TableCell>Equipo</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Fecha</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vuelos.map((vuelo) => (
                      <TableRow key={vuelo.id} hover>
                      
                        <TableCell><Typography fontWeight="bold">{vuelo.master || '-'}</Typography></TableCell>
                        <TableCell>{vuelo.origen}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {(vuelo.destino || '').split(';').map((d, i) => (
                              <Chip key={i} label={d} size="small" variant="outlined" />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>{vuelo.equipo_nombre || '-'}</TableCell>
                        <TableCell>{vuelo.cliente || '-'}</TableCell>
                        <TableCell>{vuelo.fecha}</TableCell>
                        <TableCell align="center">
                          {/* Editar solo si no tiene carga */}
                          {(vuelo.tpallets || 0) === 0 && (
                            <Tooltip title="Editar Vuelo">
                              <IconButton
                                size="small"
                                color="warning"
                                component={Link}
                                to={`/vuelos/${vuelo.awb}/editar`}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Eliminar">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(vuelo)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Card>
        ))
      )}

      {/* Volver */}
      <Box sx={{ mt: 4 }}>
        <Button
          component={Link}
          to="/aerolinea"
          variant="contained"
          color="inherit"
          startIcon={<BackIcon />}
        >
          Volver al Inicio
        </Button>
      </Box>
    </Box>
  );
};

export default GestionVuelos;




